import { IonApp, IonSplitPane } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { createSelector } from '@reduxjs/toolkit';
import { ErrorBoundary, captureException } from '@sentry/react';
import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { t } from 'ttag';

import { Provider as AppcuesProvider } from '@components/Appcues';
import Menu from '@components/Menu';
import { Meta } from '@custom-types/Response';
import useDeeplink from '@hooks/useDeeplink';
import useNetwork from '@hooks/useNetwork';
import { getSystem as getSystemFlagsFn } from '@services/flag';
import { getDeviceLocale, initLocale } from '@services/language-service';
import { getKioskColor } from '@state/event';
import { setSystem } from '@state/flag';
import { getKioskEnabled } from '@state/kiosk';
import store from '@state/store';
import { setLocale, getIsLoggedIn } from '@state/system';
import GlobalStyles from '@theme/GlobalStyles';

import Fallback from './Fallback';
import Routes from './Routes';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Dark theme variables */
import '@ionic/react/css/palettes/dark.system.css';

(async () => {
  try {
    // useLocale should be called before using ttag functions are executed
    //  https://github.com/ttag-org/ttag/issues/167
    await initLocale();
  } catch (err) {
    captureException(err);
  }
})();

const selector = createSelector(
  getIsLoggedIn,
  getKioskEnabled,
  getKioskColor,
  (isLoggedIn, kioskEnabled, kioskColor) => ({
    isLoggedIn,
    kioskEnabled,
    kioskColor,
  })
);

const App = () => {
  const dispatch = useDispatch();
  useNetwork(t`No internet connection`);
  const { isLoggedIn, kioskColor, kioskEnabled } = useSelector(selector);
  useDeeplink();

  useEffect(() => {
    const effect = async () => {
      const deviceLanguage = await getDeviceLocale();
      dispatch(setLocale(deviceLanguage));

      const systemFlags = await getSystemFlagsFn();
      dispatch(setSystem(systemFlags));
    };
    effect();
  }, [dispatch]);

  return (
    <IonApp>
      <GlobalStyles kioskColor={kioskColor} kioskEnabled={kioskEnabled} />
      <AppcuesProvider>
        <IonReactRouter>
          <IonSplitPane contentId="main">
            {!kioskEnabled && isLoggedIn && <Menu />}
            <Routes />
          </IonSplitPane>
        </IonReactRouter>
      </AppcuesProvider>
    </IonApp>
  );
};
interface FallbackProps {
  error: Error;
  componentStack: string | null;
  eventId: string | null;
  resetError(): void;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // TODO: EXP-11001 RQH is retrying on 400 as well.
      // for now we are setting retry to 0 but
      // RQH should be configure to not retry on 400/
      retry: 0,
      getNextPageParam: lastPage => {
        const {
          meta: { pagination },
        } = lastPage as { meta: Meta };
        if (!pagination) {
          return undefined;
        }

        const { page, limit, totalItemsCount } = pagination;

        // If there are no more pages, return undefined
        if ((page + 1) * limit >= totalItemsCount) {
          return undefined;
        }
        // Otherwise return the next page
        return page + 1;
      },
    },
  },
});

const fallback = ({ ...props }: FallbackProps) => <Fallback {...props} />;

const Wrapper = () => (
  <ErrorBoundary fallback={fallback}>
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <Provider store={store}>
        <App />
      </Provider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default Wrapper;
